import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { ReceiptText } from "lucide-react";

// Same formatting as pricing.jsx `inr` (kept local so the two files do not import each other).
const inr = (n) => {
  const v = Number(n || 0);
  const frac = Math.abs(v - Math.round(v)) > 0.004;
  return `₹${v.toLocaleString("en-IN", { minimumFractionDigits: frac ? 2 : 0, maximumFractionDigits: 2 })}`;
};

/**
 * GST bill review. No tax is calculated in the browser: the rate comes from the admin's billing groups and the
 * numbers come from GET /billing/gst-preview. The order itself is priced again on the server when it is created.
 */
export function useGstBill({ kind, ids = [], taxable }) {
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const key = `${kind}|${ids.join(",")}|${taxable}`;
  const seq = useRef(0);
  useEffect(() => {
    const t = Number(taxable);
    if (!Number.isFinite(t) || t < 0 || taxable === null || taxable === undefined || taxable === "") { setBill(null); setFailed(false); return undefined; }
    const my = ++seq.current;
    setLoading(true); setFailed(false);
    const timer = setTimeout(() => {
      api.get("/billing/gst-preview", { params: { kind, taxable: t, item_ids: ids.join(","), _: Date.now() } })
        .then((r) => { if (my === seq.current) setBill(r.data); })
        .catch(() => { if (my === seq.current) { setBill(null); setFailed(true); } })
        .finally(() => { if (my === seq.current) setLoading(false); });
    }, 120);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return { bill, loading, failed, total: bill ? bill.total : null };
}

/** Rebuild the same bill from a stored order / payment intent (GST snapshot taken when the order was created). */
export function billFromOrder(o) {
  if (!o) return null;
  const gstAmount = Number(o.gst_amount || 0);
  const total = Number(o.amount || 0);
  const taxable = o.taxable_amount != null ? Number(o.taxable_amount) : Math.max(0, total - gstAmount);
  return {
    taxable_amount: taxable, gst_amount: gstAmount, cgst_amount: Number(o.cgst_amount || gstAmount / 2),
    sgst_amount: Number(o.sgst_amount || gstAmount / 2), gst_percent: Number(o.gst_percent || 0),
    gst_lines: Array.isArray(o.gst_lines) ? o.gst_lines : [], gst_enabled: gstAmount > 0, total,
  };
}

const pct = (p) => (Number.isInteger(p) ? String(p) : String(Number(p.toFixed(2))));

/** GST rows + grand total. Used on its own (add-ons, extra services) and inside PriceBreakdown (plans). */
export function GstRows({ bill, loading, failed }) {
  const row = "flex items-center justify-between gap-3 py-1.5 text-sm";
  if (!bill) {
    return (
      <div className="mt-2 border-t border-border pt-3 text-sm text-muted-foreground" data-testid="gst-loading">
        {failed ? "Could not load the GST amount. Please check your connection." : loading ? "Calculating GST…" : ""}
      </div>
    );
  }
  const multi = (bill.gst_lines || []).filter((l) => l.percent > 0).length > 1;
  return (
    <div data-testid="gst-rows">
      <div className={`${row} mt-1 border-t border-dashed border-border pt-2.5`}>
        <span className="text-muted-foreground">Amount before GST</span>
        <span className="font-medium tabular-nums" data-testid="gst-taxable">{inr(bill.taxable_amount)}</span>
      </div>
      {bill.gst_amount > 0 ? (
        multi ? (
          bill.gst_lines.filter((l) => l.percent > 0).map((l, i) => (
            <div className={row} key={i}><span className="text-muted-foreground">GST {pct(l.percent)}% · {l.name}</span><span className="tabular-nums">{inr(l.gst)}</span></div>
          ))
        ) : (
          <>
            <div className={row}><span className="text-muted-foreground">CGST {pct(bill.gst_percent / 2)}%</span><span className="tabular-nums" data-testid="gst-cgst">{inr(bill.cgst_amount)}</span></div>
            <div className={row}><span className="text-muted-foreground">SGST {pct(bill.gst_percent / 2)}%</span><span className="tabular-nums" data-testid="gst-sgst">{inr(bill.sgst_amount)}</span></div>
          </>
        )
      ) : (
        <div className={row}><span className="text-muted-foreground">GST</span><span className="text-muted-foreground">Not applicable</span></div>
      )}
      <div className="mt-2 flex items-center justify-between gap-3 border-t border-border pt-3">
        <span className="font-semibold">Total to pay{bill.gst_amount > 0 ? <span className="ml-1 text-xs font-normal text-muted-foreground">(incl. GST {inr(bill.gst_amount)})</span> : null}</span>
        <span className="text-2xl font-semibold tracking-tight tabular-nums" data-testid="amount-payable-with-gst">{inr(bill.total)}</span>
      </div>
    </div>
  );
}

/**
 * Stand-alone bill review card, shown before the customer pays.
 * lines: [{ label, amount, note? }] item rows (already server prices before GST); discount: positive number.
 */
export function BillReview({ title = "Review your bill", lines = [], discount = 0, discountLabel = "Discount", bill, loading, failed, className = "" }) {
  const row = "flex items-start justify-between gap-3 py-1.5 text-sm";
  return (
    <div className={`rounded-2xl border border-border bg-card p-4 ${className}`} data-testid="bill-review">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><ReceiptText className="h-4 w-4 text-primary" />{title}</div>
      {lines.map((l, i) => (
        <div className={row} key={i}>
          <span className="min-w-0 text-muted-foreground">{l.label}{l.note ? <small className="block text-[0.6875rem]">{l.note}</small> : null}</span>
          <span className="shrink-0 tabular-nums">{inr(l.amount)}</span>
        </div>
      ))}
      {discount > 0 && <div className={row}><span className="text-muted-foreground">{discountLabel}</span><span className="font-medium text-emerald-600 dark:text-emerald-400 tabular-nums">-{inr(discount)}</span></div>}
      <GstRows bill={bill} loading={loading} failed={failed} />
    </div>
  );
}
